import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import i18n from 'i18next';
import { mapValues } from 'lodash';
import classNames from 'classnames';

import { setEcap } from '../../../../store/actions';
import { getOnboardingState, getEcapState } from '../../../../store/selectors';
import InformationIcon from '../../../utility/InformationIcon';
import ClassesProgressBar from './ClassesProgressBar';
import SpeechBubble from '../../../utility/SpeechBubble';
import { modalOpen_GTM as GTM } from '../../../../gtm/datalayers/utilDataLayers.json';
import { progressBarsStatus } from './dragDropUtil';

const getSubjectPopUpsStateKey = (key) => {
  let subjectPopUpStateKey;

  switch (key) {
    case 'English':
      subjectPopUpStateKey = 'english';
      break;
    case 'Math':
      subjectPopUpStateKey = 'math';
      break;
    case 'Laboratory Science':
      subjectPopUpStateKey = 'laboratoryScience';
      break;
    case 'Social Science':
      subjectPopUpStateKey = 'socialScience';
      break;
    case 'Fine Arts / Career & Technical Education':
      subjectPopUpStateKey = 'fineArts';
      break;
    case 'Foreign Language':
      subjectPopUpStateKey = 'foreignLanguage';
      break;
    case 'Electives':
      subjectPopUpStateKey = 'electives';
      break;
    case 'Additional Electives':
      subjectPopUpStateKey = 'additionalElectives';
      break;
    default:
      return '';
  }

  return subjectPopUpStateKey;
};

class CoreSubjectsColumn extends Component {
  constructor(props) {
    super(props);

    this.state = {
      subjectPopUps: {
        english: false,
        math: false,
        laboratoryScience: false,
        socialScience: false,
        fineArts: false,
        foreignLanguage: false,
        electives: false,
        additionalElectives: false,
      },
    };
  }

  clearSubjectPopUps = () => {
    this.setState(({ subjectPopUps }) => ({
      subjectPopUps: mapValues(subjectPopUps, () => false),
    }));
  };

  componentDidMount = () => {
    document.addEventListener('mousedown', this.clearSubjectPopUps);
  };

  componentWillUnmount = () => {
    document.removeEventListener('mousedown', this.clearSubjectPopUps);
  };

  showPopUpBubble = (subject) => {
    const stateKey = getSubjectPopUpsStateKey(subject);

    this.setState(({ subjectPopUps }) => ({
      subjectPopUps: mapValues(subjectPopUps, (val, key) =>
        key === stateKey ? true : val
      ),
    }));
  };

  checkProgressBars = (subject) => {
    if (this.props.progressByDegree)
      return progressBarsStatus(
        this.props.placedClasses,
        subject,
        this.props.progressByDegree
      );
  };

  getNumBars = (subject) => {
    if (this.props.progressByDegree) {
      return this.props.progressByDegree[subject].length;
    }
  };

  get coreSubjectsDescriptions() {
    return {
      English: i18n.t(
        'English courses provide the opportunity for students to become proficient in reading, writing, speaking and listening, and language based on individual state standards.'
      ),
      Math: i18n.t(
        'Mathematics courses develop foundational strategies that improve the conceptual learning and use of mathematical applications. Common topics include theories, properties, operations, expressions, equations, word problems and graphing.'
      ),
      'Laboratory Science': i18n.t(
        'Laboratory Science courses provide the opportunity for students to manipulate equipment, materials, or specimens, to develop skills in observation and analysis, and to discover, demonstrate, illustrate, or test scientific principles or concepts, such as chemistry, physics, earth sciences and biology.  '
      ),
      'Social Science': i18n.t(
        'Social Science courses provide the opportunity for students to study human society and social relationships. Social Science disciplines include Economics, Political Science, Psychology, Sociology, Anthropology, History, and Linguistics, among others.'
      ),
      'Fine Arts / Career & Technical Education': i18n.t(
        'Fine Arts courses provide the opportunity to gain experience and knowledge in the production and performance; analysis, interpretation, and evaluation; and historical and cultural aspects of the visual and performing arts. Career and Technical Education courses provide the opportunity to acquire technical and academic skills, make informed decisions about occupational program options, and make a seamless transition to postsecondary programs. Students will develop 21st Century workplace readiness skills and acquire knowledge and proficiency in CTE programs. Examples include courses in Marketing, Computer Technology/Applications/Science, and Sports Medicine.'
      ),
      'Foreign Language': i18n.t(
        'World Language courses will provide the opportunity to competently use a world language for written and oral communication and demonstrate a knowledge of appropriate cultural behaviors. Also includes communication using visual gestures and signs.'
      ),
      Electives: i18n.t(
        'Elective courses provide students with the opportunity to choose from a number of optional subjects or courses. Electives also include college level coursework taken in high school that does not fulfill other high school course requirements.'
      ),
      'Additional Electives': i18n.t(
        'Elective courses provide students with the opportunity to choose from a number of optional subjects or courses. Electives also include college level coursework taken in high school that does not fulfill other high school course requirements.'
      ),
    };
  }

  get combinedCoreSubjectsList() {
    return this.props.coreSubjectsListEn.map((subjectEn, index) => {
      return {
        english: subjectEn,
        translated: this.props.coreSubjectsList[index],
      };
    });
  }

  render() {
    const { subjectPopUps } = this.state;
    return (
      <div
        className={classNames({
          'core-subjects-column-container': true,
          mobile: this.props.mobile,
        })}
      >
        {this.combinedCoreSubjectsList.map((subjectInfo) => (
          <div
            className={classNames({
              'core-subject-container': true,
              tall: subjectInfo.translated.length > 25,
            })}
            key={subjectInfo.english}
          >
            <div className="core-subject-icon">
              <p className="core-subject-text">{subjectInfo.translated}</p>
              <div
                className="info-icon-container"
                onFocus={() => {
                  this.showPopUpBubble(subjectInfo.english);
                }}
                onBlur={() => {
                  this.clearSubjectPopUps();
                }}
              >
                <InformationIcon
                  parentOnClickHandler={(e) => {
                    if (['Escape'].includes(e.key)) {
                      this.clearSubjectPopUps();
                    } else {
                      this.showPopUpBubble(subjectInfo.english);
                    }
                  }}
                  color={this.props.tenant.primary}
                  modalImage={this.props.modalImage}
                  gtmObj={GTM}
                  text="information"
                  section={subjectInfo.english}
                  ariaLabel={`Core Subject: ${
                    subjectInfo.translated
                  }. Additional Information: ${
                    this.coreSubjectsDescriptions[subjectInfo.english]
                  }`}
                />
              </div>
            </div>
            {subjectPopUps[getSubjectPopUpsStateKey(subjectInfo.english)] ? (
              <SpeechBubble
                textToDisplay={
                  this.coreSubjectsDescriptions[subjectInfo.english]
                }
                bottom="70px"
                right={this.props.rightBubbleOffset || '-107px'}
                minHeight="120px"
                height="auto"
                padding="10px"
              />
            ) : null}
            <ClassesProgressBar
              numBars={this.getNumBars(subjectInfo.english)}
              tenant={this.props.tenant}
              barsFilled={this.checkProgressBars(subjectInfo.english)}
            />
          </div>
        ))}
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  const onboarding = getOnboardingState(state);
  const ecap = getEcapState(state);

  return { onboarding, ecap };
};

export default withRouter(
  connect(mapStateToProps, { setEcap })(CoreSubjectsColumn)
);
